import { useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { SensorTransport } from './SensorTransportProvider';
import { fetchAlarmedBoxes, fetchBoxStatus, fetchBoxTimeline } from '../core/_requests';
import { AUTH_LOCAL_STORAGE_KEY } from '../../../modules/auth/core/AuthHelpers';

// Configured via REACT_APP_WS_URL. If empty, the WS connection is skipped and
// the transport degrades into the same behavior as HttpPollingTransport.
const WS_URL = process.env.REACT_APP_WS_URL ?? '';

function getUserToken(): string | null {
  try {
    const raw = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Auth shape: { token, refreshToken, user, menu_roles }
    return typeof parsed?.token === 'string' ? parsed.token : null;
  } catch {
    return null;
  }
}

/**
 * Singleton WebSocket connection (one per browser tab). Multiple hook instances
 * register invalidators that fan out to react-query when a `alarm_changed`
 * event arrives. Reconnects with exponential backoff if the socket drops.
 *
 * If WS is unavailable (no URL, no token, network failure), the underlying
 * useQuery still polls at 10s — so the UI never depends on the WS being up.
 */
let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let attempt = 0;
const invalidators = new Set<(eventType: string) => void>();

function buildUrl(token: string): string {
  // Browsers can't set headers on WS upgrade; pass token in query string.
  // Server's web auth path also accepts ?token= alongside the `?as=web` flag.
  const sep = WS_URL.includes('?') ? '&' : '?';
  return `${WS_URL}${sep}as=web&token=${encodeURIComponent(token)}`;
}

function connect(): void {
  if (!WS_URL) return;
  const token = getUserToken();
  if (!token) return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return;
  }

  try {
    ws = new WebSocket(buildUrl(token));
  } catch {
    scheduleReconnect();
    return;
  }

  ws.onopen = () => {
    attempt = 0;
  };

  ws.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data);
      if (msg && typeof msg.type === 'string') {
        if (msg.type === 'alarm_changed') {
          invalidators.forEach((fn) => fn(msg.type));
        }
      }
    } catch {
      /* ignore malformed payloads */
    }
  };

  ws.onclose = () => {
    ws = null;
    scheduleReconnect();
  };

  ws.onerror = () => {
    // onclose follows; reconnect is handled there.
  };
}

function scheduleReconnect(): void {
  // No subscribers? don't reconnect.
  if (invalidators.size === 0) return;
  if (reconnectTimer) return;
  const backoff = Math.min(30_000, 1_000 * Math.pow(2, Math.min(attempt, 5)));
  attempt += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, backoff);
}

function disconnectIfIdle(): void {
  if (invalidators.size > 0) return;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (ws) {
    try { ws.close(1000, 'no subscribers'); } catch { /* ignore */ }
    ws = null;
  }
  attempt = 0;
}

function subscribe(fn: (eventType: string) => void): () => void {
  invalidators.add(fn);
  connect();
  return () => {
    invalidators.delete(fn);
    disconnectIfIdle();
  };
}

/**
 * Public API for components that aren't using the sensors-v2 SensorTransport
 * but still want to react to `alarm_changed` events (logs page, dashboard
 * widget when used as a polling fallback, etc). The singleton WS connection
 * is shared across all subscribers.
 */
export function subscribeAlarmChanged(fn: (eventType: string) => void): () => void {
  return subscribe(fn);
}

/**
 * Hook that invalidates a react-query cache key whenever an `alarm_changed`
 * event arrives over the singleton WS connection. Drop-in for any useQuery
 * that should refresh on alarm state changes.
 *
 * Pass either a string prefix (matches all keys starting with that string),
 * an array key, or a custom matcher object — all are forwarded as-is to
 * react-query's `invalidateQueries`.
 */
export function useAlarmChangedInvalidator(queryKey: string | unknown[]): void {
  const qc = useQueryClient();
  const keySig = Array.isArray(queryKey) ? JSON.stringify(queryKey) : queryKey;
  useEffect(() => {
    const unsub = subscribeAlarmChanged(() => {
      qc.invalidateQueries(queryKey as any);
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, keySig]);
}

export const webSocketTransport: SensorTransport = {
  useAlarmedBoxes(filters) {
    const qc = useQueryClient();
    useEffect(() => {
      // Invalidate the alarmed-boxes branch; react-query will refetch any
      // queries currently mounted under this key.
      const unsub = subscribe(() => {
        qc.invalidateQueries(['sensors-v2', 'alarmed-boxes']);
      });
      return unsub;
    }, [qc]);
    return useQuery(
      ['sensors-v2', 'alarmed-boxes', filters],
      () => fetchAlarmedBoxes(filters),
      {
        // Polling stays on as a safety net if the WS is down.
        refetchInterval: 10_000,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        keepPreviousData: true,
      },
    );
  },
  useBoxTimeline(box_id, filters) {
    const qc = useQueryClient();
    useEffect(() => {
      // Timeline of the focused box also benefits from instant refresh.
      const unsub = subscribe(() => {
        if (box_id !== null) {
          qc.invalidateQueries(['sensors-v2', 'box-timeline', box_id]);
        }
      });
      return unsub;
    }, [qc, box_id]);
    return useQuery(
      ['sensors-v2', 'box-timeline', box_id, filters],
      () => fetchBoxTimeline(box_id as number, filters),
      {
        enabled: box_id !== null,
        refetchInterval: 10_000,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        keepPreviousData: true,
      },
    );
  },
  useBoxStatus(box_id) {
    const qc = useQueryClient();
    useEffect(() => {
      if (box_id === null) return;
      // Header / status card / dashboard widget all read through this; invalidate
      // the specific box_id on every alarm_changed so they refresh immediately.
      const unsub = subscribe(() => {
        qc.invalidateQueries(['sensors-v2', 'box-status', box_id]);
      });
      return unsub;
    }, [qc, box_id]);
    return useQuery(
      ['sensors-v2', 'box-status', box_id],
      () => fetchBoxStatus(box_id as number),
      { enabled: box_id !== null, refetchOnWindowFocus: false },
    );
  },
};
